import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import Nav from "./components/sebyx/Nav";
import GrowthHero from "./components/sebyx/GrowthHero";
import GrowthOfferings from "./components/sebyx/GrowthOfferings";
import SelectedOutcomes from "./components/sebyx/SelectedOutcomes";
import HowWeWork from "./components/sebyx/HowWeWork";
import RegionalPresence from "./components/sebyx/RegionalPresence";
import GrowthForm from "./components/sebyx/GrowthForm";
import TrustBar from "./components/sebyx/TrustBar";
import Academy from "./components/sebyx/Academy";
import FounderBio from "./components/sebyx/FounderBio";
import ContactFooter from "./components/sebyx/ContactFooter";
import TermsOfService from "./components/sebyx/legal/TermsOfService";
import PrivacyPolicy from "./components/sebyx/legal/PrivacyPolicy";
import RefundPolicy from "./components/sebyx/legal/RefundPolicy";
import CancellationPolicy from "./components/sebyx/legal/CancellationPolicy";
import VisitingCard from "./components/sebyx/VisitingCard";
import PitchDoc from "./components/sebyx/PitchDoc";
import FrontlinePlaybook from "./components/sebyx/FrontlinePlaybook";
import FrontlineCurriculum from "./components/sebyx/FrontlineCurriculum";
import RookieHunterCurriculum from "./components/sebyx/RookieHunterCurriculum";
import FrontlineBrochure from "./components/sebyx/FrontlineBrochure";
import RookieHunterBrochure from "./components/sebyx/RookieHunterBrochure";
import { FrontlineScorecards, RookieScorecards } from "./components/sebyx/Scorecards";
import { FrontlineCertificate, RookieCertificate } from "./components/sebyx/Certificate";
import WhatsAppFloat from "./components/sebyx/WhatsAppFloat";
import { initAnalytics, trackPageview } from "./lib/analytics";

const toaster = (
  <Toaster
    theme="dark"
    position="bottom-right"
    toastOptions={{
      style: {
        background: "#12161F",
        border: "1px solid #1E293B",
        color: "#F8FAFC",
        borderRadius: 0,
        fontFamily: '"IBM Plex Sans", sans-serif',
      },
    }}
  />
);

function Landing() {
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sebyx",
    "alternateName": "Sebyx Academy",
    "url": "https://www.sebyx.com/",
    "logo": "https://www.sebyx.com/og-image.jpg",
    "description": "Sebyx is a growth partner for manufacturing and industrial companies — sales systems, demand generation, BD as a service, new territory expansion and corporate sales training. Founded by Mandar Prabhudesai.",
    "founder": {
      "@type": "Person",
      "name": "Mandar Prabhudesai",
      "sameAs": "https://www.linkedin.com/in/mandarprabhudesai/",
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-99725-54702",
      "contactType": "customer service",
      "email": "contact@sebyx.com",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi"],
    },
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "Karnataka",
      "addressCountry": "IN",
    },
  };
  return (
    <main data-testid="landing-page" className="App font-body">
      <Helmet>
        <title>Sebyx | Growth Partner for Manufacturing &amp; Industrial Companies</title>
        <meta name="description" content="Sebyx helps ₹15–100 Cr manufacturing and industrial companies turn existing sales teams into predictable revenue engines — sales systems, demand generation, BD as a service, new territory expansion and corporate sales training. Bengaluru · Nagpur · Indore." />
        <link rel="canonical" href="https://www.sebyx.com/" />
        <meta property="og:title" content="Sebyx | Growth Partner for Manufacturing &amp; Industrial Companies" />
        <meta property="og:description" content="Sales systems, demand generation, BD as a service and new-market entry for ₹15–100 Cr industrial companies. Bengaluru · Nagpur · Indore." />
        <meta property="og:url" content="https://www.sebyx.com/" />
        <meta name="twitter:title" content="Sebyx | Growth Partner for Manufacturing &amp; Industrial Companies" />
        <meta name="twitter:description" content="Sales systems, demand generation, BD as a service and new-market entry for ₹15–100 Cr industrial companies." />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>
      <Nav />
      <GrowthHero />
      <TrustBar />
      <GrowthOfferings />
      <SelectedOutcomes />
      <HowWeWork />
      <RegionalPresence />
      <GrowthForm />
      <Academy />
      <FounderBio />
      <ContactFooter />
      <WhatsAppFloat />
      {toaster}
    </main>
  );
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/visiting-card" element={<VisitingCard />} />
          <Route path="/pitch" element={<PitchDoc />} />
          <Route path="/frontline-playbook" element={<FrontlinePlaybook />} />
          <Route path="/frontline-curriculum" element={<FrontlineCurriculum />} />
          <Route path="/rookie-hunter" element={<RookieHunterCurriculum />} />
          <Route path="/frontline-brochure" element={<FrontlineBrochure />} />
          <Route path="/rookie-hunter-brochure" element={<RookieHunterBrochure />} />
          <Route path="/frontline-scorecards" element={<FrontlineScorecards />} />
          <Route path="/rookie-scorecards" element={<RookieScorecards />} />
          <Route path="/frontline-certificate" element={<FrontlineCertificate />} />
          <Route path="/rookie-certificate" element={<RookieCertificate />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
